import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  House, Users as UsersIcon, Buildings, Briefcase, LinkSimple, ClockCounterClockwise,
  Receipt, Bank, ChartLine, ShieldCheck, SignOut, Gear, UserCircleGear, EnvelopeSimple, IdentificationCard, FilePdf, List, X, RocketLaunch, PaperPlaneTilt, BookOpen, Megaphone, Calculator, Handshake,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, roles: ["admin", "analyst", "manager"], group: "Overview" },
  { to: "/reports", label: "Reports", icon: ChartLine, roles: ["admin", "analyst"], group: "Overview" },

  { to: "/proposals", label: "Proposals", icon: FilePdf, roles: ["admin", "analyst", "manager"], group: "Sales & Hiring", hint: "quotes to prospective clients" },
  { to: "/recruitment", label: "Recruitment", icon: Megaphone, roles: ["admin"], group: "Sales & Hiring", hint: "find workers for a project" },
  { to: "/packets", label: "Onboarding Packets", testId: "nav-packets", icon: IdentificationCard, roles: ["admin", "analyst", "manager"], group: "Sales & Hiring", hint: "new-worker paperwork" },
  { to: "/sales-reps", label: "Sales Reps", icon: Handshake, roles: ["admin", "analyst"], group: "Sales & Hiring", hint: "draw vs. commission, review queue" },

  { to: "/workers", label: "Workers", icon: UsersIcon, roles: ["admin", "analyst", "manager"], group: "People & Jobs" },
  { to: "/clients", label: "Clients", icon: Buildings, roles: ["admin", "analyst", "manager"], group: "People & Jobs" },
  { to: "/projects", label: "Projects", icon: Briefcase, roles: ["admin", "analyst"], group: "People & Jobs" },
  { to: "/assignments", label: "Assignments", icon: LinkSimple, roles: ["admin", "analyst"], group: "People & Jobs", hint: "who works where, at what rate" },

  { to: "/timesheets", label: "Timesheets", icon: ClockCounterClockwise, roles: ["admin", "analyst"], group: "Money" },
  { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["admin", "analyst"], group: "Money" },
  { to: "/payruns", label: "Pay Runs", icon: Bank, roles: ["admin", "analyst"], group: "Money" },
  { to: "/year-end", label: "Year-End 1099", icon: Calculator, roles: ["admin", "analyst"], group: "Money" },

  { to: "/audit", label: "Audit Trail", icon: ShieldCheck, roles: ["admin", "analyst"], group: "Records" },
  { to: "/sent-emails", label: "Sent Emails", icon: EnvelopeSimple, roles: ["admin", "analyst"], group: "Records" },

  { to: "/users", label: "Users", icon: UserCircleGear, roles: ["admin"], group: "Admin" },
  { to: "/announcements", label: "Announcements", icon: PaperPlaneTilt, roles: ["admin"], group: "Admin" },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["admin", "analyst"], group: "Admin" },
  { to: "/help", label: "Admin Guide", icon: BookOpen, roles: ["admin", "analyst"], group: "Admin" },
  // Kept routable (linked from Settings) but no longer a top-level menu item.
  { to: "/launch-readiness", label: "Needs Attention", icon: RocketLaunch, roles: ["admin"], hidden: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [showChangePw, setShowChangePw] = useState(false);
  const [open, setOpen] = useState(false);
  const doLogout = () => logout();

  // Auto-close drawer on route change (mobile)
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const activeItem = NAV.find((n) => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)));

  return (
    <div className="min-h-screen bg-slate-100 md:flex">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-slate-900 text-white px-4 py-3 flex items-center gap-3">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-1 -ml-1 text-slate-200" aria-label="Open menu">
          <List size={24} weight="bold" />
        </button>
        <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-8 w-8 rounded object-contain bg-white/10 p-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm leading-tight truncate">{activeItem?.label || "SOS"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">{user.role}</div>
        </div>
      </header>

      {/* Backdrop (mobile only) */}
      {open && (
        <div data-testid="mobile-backdrop" onClick={() => setOpen(false)}
             className="fixed inset-0 bg-black/50 z-40 md:hidden" />
      )}

      <aside
        data-testid="admin-sidebar"
        className={`fixed inset-y-0 left-0 w-72 bg-slate-900 text-slate-100 flex flex-col z-50 transform transition-transform duration-200 md:relative md:w-64 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-3">
          <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-11 w-11 rounded object-contain bg-white/5 p-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-black text-base leading-tight text-white truncate">Safety Onsite</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">Operations Console</div>
          </div>
          <button data-testid="mobile-close-btn" onClick={() => setOpen(false)}
                  className="md:hidden text-slate-300 p-1 -mr-1" aria-label="Close menu">
            <X size={22} />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
          {(() => {
            const items = NAV.filter((n) => n.roles.includes(user.role) && !n.hidden);
            const groups = [...new Set(items.map((n) => n.group))];
            return groups.map((g, i) => (
              <div key={g} className={i > 0 ? "mt-3" : ""} data-testid={`nav-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="mx-3 mb-1 px-3 py-1.5 rounded-md bg-green-600/20 border-l-4 border-green-500 text-[11px] font-extrabold uppercase tracking-[0.2em] text-green-300">{g}</div>
                {items.filter((n) => n.group === g).map(({ to, label, icon: Icon, hint, testId }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    title={hint}
                    data-testid={testId || `nav-${label.toLowerCase().replace(/ /g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? "bg-slate-800 border-l-4 border-green-500 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white border-l-4 border-transparent"
                      }`
                    }
                  >
                    <Icon size={18} weight="duotone" className="shrink-0" />
                    <span className="min-w-0">
                      <span className="block leading-tight">{label}</span>
                      {hint && <span className="block text-[10px] text-slate-500 leading-tight truncate">{hint}</span>}
                    </span>
                  </NavLink>
                ))}
              </div>
            ));
          })()}
        </nav>
        <div className="p-4 border-t border-slate-800 text-xs">
          <div className="text-slate-400">Signed in</div>
          <div className="font-medium text-white truncate">{user.email}</div>
          <div className="uppercase tracking-widest text-green-500 mt-1">{user.role}</div>
          <button
            data-testid="change-password-btn"
            onClick={() => setShowChangePw(true)}
            className="mt-3 w-full py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 transition-colors"
          >Change password</button>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
